import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Check, ExternalLink, Trash2, X } from "lucide-react";
import {
  listUserMessages,
  updateSuggestionStatus,
  deleteSuggestion,
  updateReportStatus,
  deleteReport,
  updateContactMessageStatus,
  deleteContactMessage,
} from "@/lib/admin.functions";
import { cleanTitle, formatDate, formatCompact } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/admin/inbox")({
  staticData: { sitemap: false },
  component: AdminInbox,
});

type Tab = "suggestions" | "reports" | "contacts";

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === "pending" || status === "new"
      ? "bg-amber/15 text-amber"
      : status === "rejected"
        ? "bg-ink/10 text-ink/50"
        : "bg-emerald-500/15 text-emerald-700";
  return <span className={`text-mono-caps px-2 py-0.5 ${tone}`}>{status}</span>;
}

function AdminInbox() {
  const listFn = useServerFn(listUserMessages);
  const sugStatusFn = useServerFn(updateSuggestionStatus);
  const sugDelFn = useServerFn(deleteSuggestion);
  const repStatusFn = useServerFn(updateReportStatus);
  const repDelFn = useServerFn(deleteReport);
  const conStatusFn = useServerFn(updateContactMessageStatus);
  const conDelFn = useServerFn(deleteContactMessage);
  const qc = useQueryClient();

  const [tab, setTab] = useState<Tab>("suggestions");
  const [onlyOpen, setOnlyOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "inbox"],
    queryFn: () => listFn(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "inbox"] });

  const mut = useMutation({
    mutationFn: async (a: { kind: "sug" | "rep" | "con"; action: "status" | "delete"; id: string; status?: string }) => {
      if (a.action === "delete") {
        if (a.kind === "sug") return sugDelFn({ data: { id: a.id } });
        if (a.kind === "rep") return repDelFn({ data: { id: a.id } });
        return conDelFn({ data: { id: a.id } });
      }
      if (a.kind === "sug") return sugStatusFn({ data: { id: a.id, status: a.status as any } });
      if (a.kind === "rep") return repStatusFn({ data: { id: a.id, status: a.status as any } });
      return conStatusFn({ data: { id: a.id, status: a.status as any } });
    },
    onSuccess: () => {
      invalidate();
      toast.success("Updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const suggestions = data?.suggestions ?? [];
  const reports = data?.reports ?? [];
  const openSuggestions = suggestions.filter((s: any) => s.status === "pending").length;
  const openReports = reports.filter((r: any) => r.status === "new").length;
  const contacts = data?.contacts ?? [];
  const openContacts = contacts.filter((c: any) => c.status === "new").length;
  const totalMessages = suggestions.length + reports.length + contacts.length;
  const totalOpen = openSuggestions + openReports + openContacts;

  const shownSuggestions = onlyOpen ? suggestions.filter((s: any) => s.status === "pending") : suggestions;
  const shownReports = onlyOpen ? reports.filter((r: any) => r.status === "new") : reports;
  const shownContacts = onlyOpen ? contacts.filter((c: any) => c.status === "new") : contacts;

  return (
    <div className="p-8">
      <p className="text-mono-caps text-amber mb-2">Backoffice</p>
      <h1 className="text-3xl mb-1">Inbox</h1>
      <p className="text-ink/60 mb-6 text-sm">Messages received from visitors: youtuber suggestions and video issue reports.</p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <SummaryCard label="Total messages" value={formatCompact(totalMessages)} />
        <SummaryCard label="Unhandled" value={formatCompact(totalOpen)} accent={totalOpen > 0} />
        <SummaryCard label="Suggestions" value={formatCompact(suggestions.length)} accent={openSuggestions > 0} sub={`${openSuggestions} pending`} />
        <SummaryCard label="Video reports" value={formatCompact(reports.length)} accent={openReports > 0} sub={`${openReports} new`} />
        <SummaryCard label="Contact messages" value={formatCompact(contacts.length)} accent={openContacts > 0} sub={`${openContacts} new`} />
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button
          onClick={() => setTab("suggestions")}
          className={`px-3 py-1.5 text-sm ring-hairline transition-colors ${tab === "suggestions" ? "bg-ink text-paper" : "hover:bg-secondary"}`}
        >
          Youtuber suggestions{openSuggestions ? ` (${openSuggestions})` : ""}
        </button>
        <button
          onClick={() => setTab("reports")}
          className={`px-3 py-1.5 text-sm ring-hairline transition-colors ${tab === "reports" ? "bg-ink text-paper" : "hover:bg-secondary"}`}
        >
          Video reports{openReports ? ` (${openReports})` : ""}
        </button>
        <button
          onClick={() => setTab("contacts")}
          className={`px-3 py-1.5 text-sm ring-hairline transition-colors ${tab === "contacts" ? "bg-ink text-paper" : "hover:bg-secondary"}`}
        >
          Contact messages{openContacts ? ` (${openContacts})` : ""}
        </button>
        <label className="ml-auto flex items-center gap-2 text-sm text-ink/60">
          <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} />
          Only unhandled
        </label>
      </div>

      {isLoading ? (
        <p className="text-mono-caps text-ink/40">Loading…</p>
      ) : tab === "suggestions" ? (
        <div className="ring-hairline bg-paper overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-mono-caps text-ink/50 border-b border-ink/8">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Youtuber</th>
                <th className="text-left px-3 py-2">Channel URL</th>
                <th className="text-left px-3 py-2">From</th>
                <th className="text-left px-3 py-2">Notes</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {shownSuggestions.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-ink/50">No suggestions.</td>
                </tr>
              )}
              {shownSuggestions.map((s: any) => (
                <tr key={s.id} className="border-b border-ink/5 align-top">
                  <td className="px-3 py-2 whitespace-nowrap text-ink/60">{formatDate(s.created_at)}</td>
                  <td className="px-3 py-2 font-medium">{s.youtuber_name}</td>
                  <td className="px-3 py-2 max-w-[220px] truncate">
                    <a href={s.channel_url} target="_blank" rel="noreferrer" className="text-amber hover:underline inline-flex items-center gap-1">
                      {s.channel_url} <ExternalLink className="size-3 shrink-0" />
                    </a>
                  </td>
                  <td className="px-3 py-2 text-ink/60">
                    {s.suggester_name || "—"}
                    {s.suggester_email ? <div className="text-xs text-ink/45">{s.suggester_email}</div> : null}
                  </td>
                  <td className="px-3 py-2 max-w-[280px] text-ink/70">{s.notes || "—"}</td>
                  <td className="px-3 py-2"><StatusBadge status={s.status} /></td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        title="Mark as done"
                        onClick={() => mut.mutate({ kind: "sug", action: "status", id: s.id, status: "done" })}
                        className="p-1.5 hover:bg-secondary text-ink/60"
                      >
                        <Check className="size-4" />
                      </button>
                      <button
                        title="Reject"
                        onClick={() => mut.mutate({ kind: "sug", action: "status", id: s.id, status: "rejected" })}
                        className="p-1.5 hover:bg-secondary text-ink/60"
                      >
                        <X className="size-4" />
                      </button>
                      <button
                        title="Delete"
                        onClick={() => {
                          if (confirm("Delete this suggestion?")) mut.mutate({ kind: "sug", action: "delete", id: s.id });
                        }}
                        className="p-1.5 hover:bg-secondary text-ink/40 hover:text-red-600"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : tab === "reports" ? (
        <div className="ring-hairline bg-paper overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-mono-caps text-ink/50 border-b border-ink/8">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Video</th>
                <th className="text-left px-3 py-2">Issue</th>
                <th className="text-left px-3 py-2">Message</th>
                <th className="text-left px-3 py-2">From</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {shownReports.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-ink/50">No reports.</td>
                </tr>
              )}
              {shownReports.map((r: any) => (
                <tr key={r.id} className="border-b border-ink/5 align-top">
                  <td className="px-3 py-2 whitespace-nowrap text-ink/60">{formatDate(r.created_at)}</td>
                  <td className="px-3 py-2 max-w-[260px]">
                    {r.video ? (
                      <Link
                        to="/admin/videos/$id"
                        params={{ id: r.video.id }}
                        className="hover:text-amber transition-colors line-clamp-2"
                      >
                        {cleanTitle(r.video.title)}
                      </Link>
                    ) : (
                      <span className="text-ink/40">Deleted video</span>
                    )}
                  </td>
                  <td className="px-3 py-2"><span className="text-mono-caps text-ink/60">{r.issue_type}</span></td>
                  <td className="px-3 py-2 max-w-[320px] text-ink/70">{r.message}</td>
                  <td className="px-3 py-2 text-ink/60">{r.reporter_email || "—"}</td>
                  <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        title="Mark as resolved"
                        onClick={() => mut.mutate({ kind: "rep", action: "status", id: r.id, status: "resolved" })}
                        className="p-1.5 hover:bg-secondary text-ink/60"
                      >
                        <Check className="size-4" />
                      </button>
                      <button
                        title="Reject"
                        onClick={() => mut.mutate({ kind: "rep", action: "status", id: r.id, status: "rejected" })}
                        className="p-1.5 hover:bg-secondary text-ink/60"
                      >
                        <X className="size-4" />
                      </button>
                      <button
                        title="Delete"
                        onClick={() => {
                          if (confirm("Delete this report?")) mut.mutate({ kind: "rep", action: "delete", id: r.id });
                        }}
                        className="p-1.5 hover:bg-secondary text-ink/40 hover:text-red-600"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="ring-hairline bg-paper overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-mono-caps text-ink/50 border-b border-ink/8">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Subject</th>
                <th className="text-left px-3 py-2">Message</th>
                <th className="text-left px-3 py-2">From</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {shownContacts.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-ink/50">No contact messages.</td>
                </tr>
              )}
              {shownContacts.map((c: any) => (
                <tr key={c.id} className="border-b border-ink/5 align-top">
                  <td className="px-3 py-2 whitespace-nowrap text-ink/60">{formatDate(c.created_at)}</td>
                  <td className="px-3 py-2 font-medium max-w-[220px]">{c.subject || "—"}</td>
                  <td className="px-3 py-2 max-w-[420px] text-ink/70 whitespace-pre-wrap">{c.message}</td>
                  <td className="px-3 py-2 text-ink/60">
                    {c.name || "—"}
                    {c.email ? (
                      <div className="text-xs">
                        <a href={`mailto:${c.email}`} className="text-amber hover:underline">{c.email}</a>
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2"><StatusBadge status={c.status} /></td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        title="Mark as resolved"
                        onClick={() => mut.mutate({ kind: "con", action: "status", id: c.id, status: "resolved" })}
                        className="p-1.5 hover:bg-secondary text-ink/60"
                      >
                        <Check className="size-4" />
                      </button>
                      <button
                        title="Reject"
                        onClick={() => mut.mutate({ kind: "con", action: "status", id: c.id, status: "rejected" })}
                        className="p-1.5 hover:bg-secondary text-ink/60"
                      >
                        <X className="size-4" />
                      </button>
                      <button
                        title="Delete"
                        onClick={() => {
                          if (confirm("Delete this message?")) mut.mutate({ kind: "con", action: "delete", id: c.id });
                        }}
                        className="p-1.5 hover:bg-secondary text-ink/40 hover:text-red-600"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent, sub }: { label: string; value: string; accent?: boolean; sub?: string }) {
  return (
    <div className={`p-4 ring-hairline ${accent ? "bg-amber/5" : "bg-paper"}`}>
      <div className="text-mono-caps text-ink/50 text-[10px] mb-1.5">{label}</div>
      <div className="font-serif text-2xl">
        {value}
        {sub ? <span className="text-xs text-ink/40 font-sans ml-2">{sub}</span> : null}
      </div>
    </div>
  );
}
